import React, { useEffect, useState } from "react";
import { useAppSelector } from "app/store";
import { useGaiaObjectUiContent } from "features/ui-content/hooks";
import {
  Box,
  Button,
  ButtonDropdown,
  ButtonDropdownProps,
  ColumnLayout,
  Container,
  Header,
  Icon,
  Input,
  Select,
  SpaceBetween,
  Spinner,
  SplitPanel,
} from "@amzn/awsui-components-react";
import { FilePreview } from "components/file-preview";
import { isFileTypePreviewable } from "components/file-preview/utils";
import { FilePreviewSplitPanelProps } from "./definitions";
import { useFilePreviewSplitPanel } from "./hooks";
import { Document } from "models/schemas";
import {
  useGetDocumentByIdQuery,
  useDownloadUrlQuery,
  useLazyDownloadUrlQuery,
  useDeleteDocumentsMutation,
  useUpdateDocumentsMutation,
  useUpdateDocumentsForMoveMutation,
} from "components/documents-table/endpoints";
import { useNotifications } from "features/notifications";
import { datePopOver } from "utils/date-format";
import { ValueWithLabel } from "utils/value-with-label";
import { CommonDeleteModal } from "components/common-reuse-components/common-delete-modal";
import MoveFoldersModal from "components/move-folders-modal";
import { useFileOperations } from "components/documents-table/file-operations";
import { useListDocumentsQuery } from "components/documents-table/endpoints";
import {
  useRegisterDocumentMutation,
  useUploadUrlMutation,
} from "components/create-audit-form/endpoints";
import { getFileNameWithExtension } from "components/documents-table/utils";
import {
  createVersionLabels,
  checkVersionCollision,
  VersionCollisionInfo,
} from "components/documents-table/version-utils";
import styles from "components/documents-table/style.module.scss";
import { RegisterDocumentRequest } from "components/create-audit-form/definitions";
import { GaiaConstants } from "utils/gaia-constants";
import VersionConfirmationModal from "components/documents-table/VersionConfirmationModal";

const FilePreviewSplitPanel: React.FC<FilePreviewSplitPanelProps> = ({
  documentId,
  isFilesTab,
  gaiaObjectType,
}) => {
  const { addNotification } = useNotifications();
  const uiContent: any = useGaiaObjectUiContent(gaiaObjectType);
  const { setSplitPanelOpen, setSelectedDocumentId } =
    useFilePreviewSplitPanel();
  const [deleteDocCall] = useDeleteDocumentsMutation();
  const [updateDocCall] = useUpdateDocumentsMutation();
  const [updateDocumentForMoveCall] = useUpdateDocumentsForMoveMutation();
  const [lazyDownloadUrlCall] = useLazyDownloadUrlQuery();

  const [document, setDocument] = useState<Document | null>(null);
  const [url, setUrl] = useState<string>("");
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [docToRename, setDocToRename] = useState<Document | undefined>();
  const [fileName, setFileName] = useState("");
  const [selectedDocumentType, setSelectedDocumentType] = useState("");
  const [isEditingType, setIsEditingType] = useState(false);
  const [isTeamFileLevel, setIsTeamFileLevel] = useState(false);
  const [moveModalVisible, setMoveModalVisible] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<Document | null>(null);
  const [versionDeleteModalVisible, setVersionDeleteModalVisible] =
    useState(false);
  const [versionToDelete, setVersionToDelete] = useState<Document | null>(null);
  const [versionRestoreModalVisible, setVersionRestoreModalVisible] =
    useState(false);
  const [versionToRestore, setVersionToRestore] = useState<Document | null>(
    null
  );
  const [versionConfirmModalVisible, setVersionConfirmModalVisible] =
    useState(false);
  const [versionCollisions, setVersionCollisions] = useState<
    VersionCollisionInfo[]
  >([]);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  const teamName = useAppSelector((state) => state.audits.teamName);
  const currentUser = useAppSelector((state) => state.auth.user.alias);
  const documentCollection = useAppSelector(
    (state) => state.filePreviewSplitPanel.documentCollection
  );
  const allVersions =
    document && documentCollection
      ? documentCollection.getVersionsForDocument(document)
      : [];
  const olderVersions = allVersions.filter((v) => !v.isLatest);

  const [registerDocCall] = useRegisterDocumentMutation();
  const [uploadUrlCall] = useUploadUrlMutation();

  const { data: documentsData } = useListDocumentsQuery(
    { parentId: teamName },
    { skip: !isTeamFileLevel || !teamName }
  );

  // Fetch document data
  const { data: docData, isLoading: isDocumentLoading } =
    useGetDocumentByIdQuery(
      { documentId: documentId! },
      {
        refetchOnMountOrArgChange: true,
        skip: !documentId,
      }
    );

  // Fetch download URL for preview
  const currentDocumentId = selectedVersion?.documentId || documentId!;
  const { data: downloadUrl } = useDownloadUrlQuery(
    { documentId: currentDocumentId, preview: true },
    {
      skip: !documentId,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: false,
      refetchOnReconnect: false,
      pollingInterval: 0,
    }
  );

  useEffect(() => {
    if (docData?.document) {
      // Handle both array and single document responses
      const doc = Array.isArray(docData.document)
        ? docData.document[0]
        : docData.document;
      setDocument(doc);
      setSelectedDocumentType(doc.documentType);
      setIsTeamFileLevel(doc.documentType === "Team");
    }
  }, [docData]);

  useEffect(() => {
    if (downloadUrl) {
      setUrl(downloadUrl.url);
    }
  }, [downloadUrl]);

  // Initialize file operations hook
  const { moveFiles } = useFileOperations({
    uploadUrlCall,
    registerDocCall,
    deleteDocCall,
    updateDocCall,
    updateDocumentForMoveCall,
    lazyDownloadUrlCall,
    ingestDocuments: undefined,
    deleteIngestedDocuments: undefined,
    addNotification,
    teamName,
    currentUser,
    parentId: document?.parentId || "",
    gaiaObjectId: "",
    category: "team-files",
    displayMode: "flat",
    setDocs: () => {},
    setSelectedItems: () => {},
    setSingleDoc: () => {},
    setDeleteDocModalIsVisible: () => {},
    setDocToRename: () => {},
    setFileName: () => {},
  });

  // Handle path move operation
  const handlePathMove = async (files: Document[], targetParentId: string) => {
    await moveFiles(files, targetParentId, documentCollection || undefined);
    // Update local document state after successful move
    setDocument((prev) =>
      prev ? { ...prev, parentId: targetParentId } : null
    );
    setMoveModalVisible(false);
    // Close the split panel
    setSplitPanelOpen(false);
    setSelectedDocumentId(null);
  };

  /**
   * Helper function to handle operations that may cause version collisions.
   * Checks for collisions and shows confirmation modal if needed, or executes the action directly.
   */
  const handleWithVersionCollisionCheck = async (
    targetDocument: Document,
    changes: Partial<Document>,
    executeAction: () => Promise<void>
  ) => {
    if (documentCollection) {
      const collision = checkVersionCollision(
        targetDocument,
        changes,
        documentCollection
      );

      if (collision.hasCollision) {
        setVersionCollisions([collision]);
        setPendingAction(() => executeAction);
        setVersionConfirmModalVisible(true);
        return;
      }
    }

    await executeAction();
  };

  const handleDelete = async () => {
    if (!document) return;

    try {
      await deleteDocCall({
        documentIds: allVersions.map((version) => version.documentId),
      }).unwrap();

      addNotification({
        type: "success",
        header: "File deleted.",
        dismissible: true,
        timeout: 5000,
      });
    } catch (e) {
      addNotification({
        type: "error",
        header: "File delete failed.",
        dismissible: true,
        timeout: 5000,
      });
    }
  };

  const handleRename = async () => {
    if (!docToRename || !fileName.trim()) return;

    await handleWithVersionCollisionCheck(
      docToRename,
      { fileName: fileName.trim() },
      executeRename
    );
  };

  const executeRename = async () => {
    if (!docToRename || !fileName.trim()) return;

    try {
      await updateDocCall({
        documentIds: allVersions.map((version) => version.documentId),
        fileName: fileName.trim(),
      }).unwrap();

      setDocument((prev) =>
        prev ? { ...prev, fileName: fileName.trim() } : null
      );
      setDocToRename(undefined);
      setFileName("");

      addNotification({
        type: "success",
        header: "File renamed successfully.",
        dismissible: true,
        timeout: 5000,
      });
    } catch (e) {
      addNotification({
        type: "error",
        header: "File rename failed.",
        dismissible: true,
        timeout: 5000,
      });
    }
  };

  const handleDocumentTypeChange = async (newType: string) => {
    if (!document) return;

    await handleWithVersionCollisionCheck(
      document,
      { documentType: newType },
      async () => executeDocumentTypeChange(newType)
    );
  };

  const executeDocumentTypeChange = async (newType: string) => {
    if (!document) return;

    try {
      await updateDocCall({
        documentIds: allVersions.map((version) => version.documentId),
        documentType: newType,
      }).unwrap();

      setDocument((prev) => (prev ? { ...prev, documentType: newType } : null));
      setSelectedDocumentType(newType);
      setIsEditingType(false);

      addNotification({
        type: "success",
        header: "Document type updated successfully.",
        dismissible: true,
        timeout: 5000,
      });
    } catch (e) {
      addNotification({
        type: "error",
        header: "Document type update failed.",
        dismissible: true,
        timeout: 5000,
      });
    }
  };

  const handleDownload = async () => {
    if (!document?.s3Prefix) return;

    try {
      const response = await lazyDownloadUrlCall({
        documentId: document.documentId,
        preview: false,
      }).unwrap();

      if (response?.url) {
        window.location.href = response.url;
      }
    } catch (e) {
      addNotification({
        type: "error",
        header: "File download failed.",
        dismissible: true,
        timeout: 5000,
      });
    }
  };

  const handleOpenInNewTab = () => {
    if (!url) {
      addNotification({
        type: "error",
        header: "Preview URL not available.",
        dismissible: true,
        timeout: 5000,
      });
      return;
    }
    window.open(url, "_blank");
  };

  const handleVersionChange = (versionId: string | undefined) => {
    if (!versionId || versionId === document?.documentId) {
      // Selected current version
      setSelectedVersion(null);
    } else {
      // Selected non-current version
      const version = allVersions.find((v) => v.documentId === versionId);
      if (version) {
        setSelectedVersion(version);
      }
    }
  };

  const handleVersionRestoreClick = () => {
    if (selectedVersion) {
      setVersionToRestore(selectedVersion);
      setVersionRestoreModalVisible(true);
    }
  };

  const handleVersionRestore = async () => {
    if (!versionToRestore || !document) return;

    try {
      // Create a new document with all fields from the version to restore
      const uploadDocRequest: RegisterDocumentRequest = {
        parentId: versionToRestore.parentId,
        teamName: versionToRestore.teamName,
        fileName: versionToRestore.fileName,
        documentType: versionToRestore.documentType,
        securityLevel: document.securityLevel,
        s3Prefix: versionToRestore.s3Prefix,
        externalUrl: versionToRestore.externalUrl,
        extentionType: versionToRestore.extentionType,
        size: versionToRestore.size,
      };
      if (versionToRestore.auditId) {
        uploadDocRequest.auditId = versionToRestore.auditId;
      } else if (versionToRestore.gaiaObjectId) {
        uploadDocRequest.gaiaObjectId = versionToRestore.gaiaObjectId;
      }

      await registerDocCall(uploadDocRequest).unwrap();

      addNotification({
        type: "success",
        header: "Version restored successfully",
        dismissible: true,
        timeout: 5000,
      });

      setVersionRestoreModalVisible(false);
      setVersionToRestore(null);
      setSelectedVersion(null);
    } catch (error) {
      addNotification({
        type: "error",
        header: "Version restoration failed",
        dismissible: true,
        timeout: 5000,
      });
    }
  };

  const handleVersionDeleteClick = () => {
    if (selectedVersion) {
      setVersionToDelete(selectedVersion);
      setVersionDeleteModalVisible(true);
    }
  };

  const handleVersionDelete = async () => {
    if (!versionToDelete) return;

    try {
      await deleteDocCall({
        documentIds: [versionToDelete.documentId],
      }).unwrap();

      addNotification({
        type: "success",
        header: "Version deleted successfully",
        dismissible: true,
        timeout: 5000,
      });

      setSelectedVersion(null);
      setVersionDeleteModalVisible(false);
      setVersionToDelete(null);
    } catch (error) {
      addNotification({
        type: "error",
        header: "Version delete failed",
        dismissible: true,
        timeout: 5000,
      });
    }
  };

  const handleVersionConfirm = () => {
    if (pendingAction) {
      pendingAction();
      setPendingAction(null);
    }
    setVersionCollisions([]);
  };

  const handleActionClick = (
    event: CustomEvent<ButtonDropdownProps.ItemClickDetails>
  ) => {
    const actionId = event.detail.id;
    const firstDotIndex = actionId.indexOf(".");
    const action = actionId.substring(0, firstDotIndex);

    switch (action) {
      case "open":
        handleOpenInNewTab();
        break;
      case "ed":
        if (document) {
          setDocToRename(document);
          setFileName(document.fileName);
        }
        break;
      case "rm":
        setDeleteModalVisible(true);
        break;
    }
  };

  const getHeaderTitle = () => {
    if (!document) return "File Preview";
    if (document.documentId === docToRename?.documentId) {
      return "Renaming file...";
    }
    return getFileNameWithExtension(document);
  };

  const renderFileNameEditor = () => {
    if (!document || document.documentId !== docToRename?.documentId)
      return null;

    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          marginBottom: "16px",
          width: "100%",
        }}
      >
        <div style={{ flex: 1, width: "100%" }}>
          <Input
            onChange={({ detail }) => setFileName(detail.value)}
            value={fileName}
          />
        </div>
        <span>{document.extentionType}</span>
        <span
          onClick={() => {
            setDocToRename(undefined);
          }}
          className={styles.renameButton}
        >
          <Icon name="close"></Icon>
        </span>
        <span onClick={handleRename} className={styles.renameButton}>
          <Icon name="check"></Icon>
        </span>
      </div>
    );
  };

  const renderDocumentType = () => {
    if (!document) return null;

    if (isEditingType) {
      const selectOptions =
        uiContent.documentTypeOptions?.map((option: any) => ({
          label: option.label,
          value: option.value,
        })) || [];

      const selectedOption =
        selectOptions.find((opt: any) => opt.value === selectedDocumentType) ||
        null;

      return (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Select
            selectedOption={selectedOption}
            onChange={({ detail }) =>
              handleDocumentTypeChange(detail.selectedOption?.value || "")
            }
            options={selectOptions}
            placeholder="Select document type"
          />
          <Button
            variant="icon"
            iconName="close"
            onClick={() => setIsEditingType(false)}
          />
        </div>
      );
    }

    const currentOption = uiContent.documentTypeOptions?.find(
      (opt: any) => opt.value === selectedDocumentType
    );

    return (
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <span>{currentOption?.label || selectedDocumentType}</span>
        <Button
          variant="icon"
          iconName="edit"
          onClick={() => setIsEditingType(true)}
        />
      </div>
    );
  };

  const renderPath = () => {
    if (!document || !isTeamFileLevel) return null;

    const currentPath = `/${document.parentId
      .split("/")
      .filter(Boolean)
      .slice(1)
      .join("/")}`;

    return (
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <span>{currentPath || "/"}</span>
        <Button
          variant="icon"
          iconName="edit"
          onClick={() => setMoveModalVisible(true)}
          ariaLabel="Edit file location"
        />
      </div>
    );
  };

  const handleVersionActionClick = (
    event: CustomEvent<ButtonDropdownProps.ItemClickDetails>
  ) => {
    const actionId = event.detail.id;

    switch (actionId) {
      case "download":
        const docToDownload = selectedVersion || document;
        if (docToDownload) {
          lazyDownloadUrlCall({
            documentId: docToDownload.documentId,
            preview: false,
          })
            .unwrap()
            .then((response) => {
              if (response?.url) {
                window.location.href = response.url;
              }
            })
            .catch(() => {
              addNotification({
                type: "error",
                header: "Version download failed",
                dismissible: true,
                timeout: 5000,
              });
            });
        }
        break;
      case "restore":
        handleVersionRestoreClick();
        break;
      case "delete":
        const versionToDelete = selectedVersion || document;
        if (versionToDelete) {
          setVersionToDelete(versionToDelete);
          setVersionDeleteModalVisible(true);
        }
        break;
    }
  };

  const renderVersionSelector = () => {
    if (!document || !documentCollection) return null;

    if (allVersions.length <= 1) return null;

    const versionLabels = createVersionLabels(allVersions);

    // Auto-select current version if no version is explicitly selected
    const selectedVersionId =
      selectedVersion?.documentId || document.documentId;

    return (
      <ValueWithLabel label="Versions">
        <SpaceBetween direction="horizontal" size="xs">
          <div style={{ minWidth: "200px" }}>
            <Select
              selectedOption={
                versionLabels.find((v) => v.value === selectedVersionId) || null
              }
              onChange={({ detail }) =>
                handleVersionChange(detail.selectedOption?.value)
              }
              options={versionLabels}
              placeholder="Select version"
            />
          </div>
          {olderVersions.length > 0 && (
            <ButtonDropdown
              items={[
                {
                  id: "download",
                  text: "Download",
                  disabled: false,
                },
                {
                  id: "restore",
                  text: "Restore",
                  disabled: !selectedVersion,
                  disabledReason: "This is the current version",
                },
                {
                  id: "delete",
                  text: "Delete",
                  disabled: false,
                },
              ]}
              onItemClick={handleVersionActionClick}
            >
              Actions
            </ButtonDropdown>
          )}
        </SpaceBetween>
      </ValueWithLabel>
    );
  };

  if (!documentId) {
    return (
      <SplitPanel
        header="File Preview"
        i18nStrings={{
          closeButtonAriaLabel: "Close panel",
          openButtonAriaLabel: "Open panel",
        }}
        closeBehavior={isFilesTab ? "collapse" : "hide"}
      >
        <Box textAlign="center" padding="l">
          Select a file to preview
        </Box>
      </SplitPanel>
    );
  }
  return (
    <>
      <SplitPanel
        header={getHeaderTitle()}
        i18nStrings={{
          closeButtonAriaLabel: "Close panel",
          openButtonAriaLabel: "Open panel",
        }}
        closeBehavior={isFilesTab ? "collapse" : "hide"}
        hidePreferencesButton
      >
        {renderFileNameEditor()}
        {isDocumentLoading ? (
          <Container>
            <Box textAlign="center">
              <Spinner /> Loading document...
            </Box>
          </Container>
        ) : document ? (
          <SpaceBetween direction="vertical" size="l">
            <Container
              header={
                <Header
                  variant="h2"
                  actions={
                    <SpaceBetween direction="horizontal" size="xs">
                      <ButtonDropdown
                        items={[
                          {
                            id: `open.${document.documentId}`,
                            text: "Open in new tab",
                            disabled: !isFileTypePreviewable(
                              document.extentionType
                            ),
                            disabledReason:
                              "Preview not available for this file type.",
                          },
                          {
                            id: `ed.${document.documentId}`,
                            text: "Rename",
                          },
                          {
                            id: `rm.${document.documentId}`,
                            text: "Delete",
                          },
                        ]}
                        onItemClick={handleActionClick}
                      >
                        Actions
                      </ButtonDropdown>
                      <Button onClick={handleDownload} variant="primary">
                        Download
                      </Button>
                    </SpaceBetween>
                  }
                >
                  Details
                </Header>
              }
            >
              <ColumnLayout columns={2} variant="text-grid">
                <SpaceBetween direction="vertical" size="l">
                  {!isTeamFileLevel ? (
                    <ValueWithLabel label="Type">
                      {renderDocumentType()}
                    </ValueWithLabel>
                  ) : (
                    <ValueWithLabel label="Path">{renderPath()}</ValueWithLabel>
                  )}
                  <ValueWithLabel label="File size">
                    {document.size}
                  </ValueWithLabel>
                </SpaceBetween>
                <SpaceBetween direction="vertical" size="l">
                  <ValueWithLabel label="Last updated">
                    {datePopOver(Number(document.updatedAt))}
                  </ValueWithLabel>
                  <ValueWithLabel label="Updated by">
                    {document.updatedBy}
                  </ValueWithLabel>
                </SpaceBetween>
              </ColumnLayout>
              <br />
              {renderVersionSelector()}
            </Container>

            <Container header={<Header variant="h2">Preview</Header>}>
              {url && document.fileName && (
                <FilePreview
                  type={document.extentionType}
                  fileName={document.fileName}
                  presignedUrl={url}
                />
              )}
            </Container>
          </SpaceBetween>
        ) : (
          <Container>
            <Box textAlign="center">Document not found</Box>
          </Container>
        )}
      </SplitPanel>

      <CommonDeleteModal
        setVisible={setDeleteModalVisible}
        visible={deleteModalVisible}
        onDelete={handleDelete}
        items={document}
        title={
          olderVersions.length > 0 ? "Delete all versions?" : "Delete file?"
        }
        message={
          olderVersions.length > 0 ? (
            <>
              Permanently delete <b>all versions</b> of {document?.fileName}?
            </>
          ) : (
            <>Permanently delete {document?.fileName}?</>
          )
        }
      />
      {isTeamFileLevel && document && (
        <MoveFoldersModal
          visible={moveModalVisible}
          setVisible={setMoveModalVisible}
          selectedFiles={[document]}
          allDocuments={documentsData?.documents || []}
          teamName={teamName}
          onMove={handlePathMove}
          documentCollection={documentCollection || undefined}
        />
      )}

      <CommonDeleteModal
        setVisible={setVersionDeleteModalVisible}
        visible={versionDeleteModalVisible}
        onDelete={handleVersionDelete}
        items={versionToDelete}
        title="Delete version?"
        message={
          <>Permanently delete this version of {versionToDelete?.fileName}?</>
        }
      />
      <CommonDeleteModal
        setVisible={setVersionRestoreModalVisible}
        visible={versionRestoreModalVisible}
        onDelete={handleVersionRestore}
        items={versionToRestore}
        title="Restore version?"
        deleteButtonText="Restore"
        message={
          <>
            Restore this version of {versionToRestore?.fileName}? This will make
            it the current version. All other versions will remain unchanged.
          </>
        }
      />
      <VersionConfirmationModal
        visible={versionConfirmModalVisible}
        setVisible={setVersionConfirmModalVisible}
        collisions={versionCollisions}
        onConfirm={handleVersionConfirm}
      />
    </>
  );
};

export default FilePreviewSplitPanel;
